// @ts-nocheck
/**
 * Training route — thin UI shell.
 *
 * All state, side-effects, and data fetching are managed by:
 *   src/hooks/use-training.ts  (useTraining hook)
 *   src/lib/api/training.ts    (pure supabase functions)
 *   src/lib/utils.ts           (computeWatchPercent)
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  PlayCircle,
  CheckCircle,
  Lock,
  BookOpen,
  AlertTriangle,
  GraduationCap,
  ShieldAlert,
  ArrowRight,
  Award,
  Loader2,
  Clock,
  ExternalLink,
} from "lucide-react";
import { CardSkeleton } from "@/components/loading";
import { LazyVideoPlayer as VideoPlayer } from "@/components/lazy-video-player";
import { YouTubePlayer } from "@/components/youtube-player";
import { getCertificates } from "@/lib/api";
import { useTraining } from "@/hooks/use-training";
import { useStudentDashboard } from "@/hooks/use-student-dashboard";
import type { TrackGroup } from "@/hooks/use-student-dashboard";
import { useAuth } from "@/hooks/use-auth";
import { useVideoCheckpoints } from "@/hooks/use-video-checkpoints";
import {
  fetchCourseCheckpoints,
  fetchModuleUnlockStatuses,
  fetchAvailableExams,
  STUDENT_QUERY_OPTIONS,
  STUDENT_QUERY_KEYS,
  invalidateAllStudentState,
} from "@/lib/api";
import { incrementActiveWatch, verifyModuleEmbeddable } from "@/lib/api/training";
import { sanitizeUrlValue } from "@/lib/utils";
import type { Checkpoint } from "@/lib/types";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/training")({
  component: TrainingPage,
});

/** Extract a YouTube video ID from any common YouTube URL shape. */
export function getYouTubeId(url: string): string | null {
  // Sanitized first: a stored value wrapping the link in the quotes of a spreadsheet cell makes
  // `new URL()` throw, and this function's null then silently routes the module to video.js.
  // The API layer cleans this too — repeated here because this is exported and called directly.
  const cleanedUrl = sanitizeUrlValue(url);
  if (!cleanedUrl) return null;
  if (/^[A-Za-z0-9_-]{11}$/.test(cleanedUrl)) return cleanedUrl;

  try {
    const parsed = new URL(cleanedUrl);
    const host = parsed.hostname.replace(/^m\./, "").replace(/^www\./, "");
    if (host === "youtu.be") {
      const id = parsed.pathname.split("/").filter(Boolean)[0];
      return id && /^[A-Za-z0-9_-]{11}$/.test(id) ? id : null;
    }
    if (host === "youtube.com" || host === "youtube-nocookie.com") {
      const v = parsed.searchParams.get("v");
      if (v && /^[A-Za-z0-9_-]{11}$/.test(v)) return v;
      const segments = parsed.pathname.split("/").filter(Boolean);
      const idx = segments.findIndex((s) => ["embed", "shorts", "live"].includes(s));
      if (idx !== -1 && segments[idx + 1] && /^[A-Za-z0-9_-]{11}$/.test(segments[idx + 1])) {
        return segments[idx + 1];
      }
    }
  } catch {
    // not a valid absolute URL, fall through
  }
  return null;
}

function TrainingPage() {
  const {
    modulesWithStatus,
    isLoading,
    selectedModule,
    videoProgress,
    serverResumeSeconds,
    openModule,
    closeModule,
    setVideoProgress,
    setIsPlaying,
    setActiveVideoDuration,
    handleVideoEnd,
    handleTimeUpdate,
    completeCurrentModule,
    markResourceOpened,
    registerSeekTo,
    registerPause,
    registerPlay,
  } = useTraining();

  // ── Un-embeddable YouTube videos ──────────────────────────────────────────
  // The player can only report that playback failed; it is not evidence. When it reports the
  // embed-blocked codes (101/150) we ask the SERVER to re-verify with YouTube. If YouTube confirms
  // it, the server persists the fact and starts returning requires_manual_completion on the module,
  // and refetching is what makes the fallback panel and the Complete button appear — so the UI is
  // driven by the server's decision rather than by the client's own diagnosis.
  const trainingQc = useQueryClient();
  const verifiedModulesRef = useRef<Set<string>>(new Set());

  const handleUnplayable = useCallback(
    async (moduleId: string, info: { code?: number; embedBlocked: boolean }) => {
      // Only the embed-blocked codes are recoverable. A removed/private/invalid video (100, 2) is a
      // genuine content fault the player already explains, and must not be talked into a waiver.
      if (!info.embedBlocked) return;
      // The player can fire onError more than once for one module (retry, remount). Verify once per
      // module per page visit so a flapping video can't spin the endpoint.
      if (verifiedModulesRef.current.has(moduleId)) return;
      verifiedModulesRef.current.add(moduleId);

      try {
        const status = await verifyModuleEmbeddable(moduleId);
        if (status.requires_manual_completion) {
          await trainingQc.invalidateQueries({ queryKey: STUDENT_QUERY_KEYS.trainingModules });
          toast.info(status.message);
        } else {
          // YouTube says it IS embeddable, so the failure is local (ad-blocker, extension, network
          // filter). Saying so is much more useful than a generic "couldn't play".
          toast.warning(status.message);
          // Allow another attempt after a genuine retry — this outcome is not a settled fact.
          verifiedModulesRef.current.delete(moduleId);
        }
      } catch {
        verifiedModulesRef.current.delete(moduleId);
        toast.error("We couldn't check this video with YouTube. Please try again.");
      }
    },
    [trainingQc],
  );

  const { trackGroups, standaloneCourses, completedCourses = [] } = useStudentDashboard() as any;
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);

  // Auto-select the first course on load (preferring the first uncompleted one)
  useEffect(() => {
    if (!selectedCourseId) {
      let firstUncompletedId: string | null = null;
      for (const tg of trackGroups) {
        // "Uncompleted" means the server's is_completed, not the module bar — a course whose
        // lessons are all watched but whose final exam is unpassed is still the one to land on.
        const found = tg.courses.find((c) => !c.is_completed);
        if (found) {
          firstUncompletedId = found.course_id;
          break;
        }
      }

      const firstId =
        firstUncompletedId ||
        trackGroups[0]?.courses[0]?.course_id ||
        standaloneCourses[0]?.course_id ||
        completedCourses[0]?.course_id ||
        null;

      if (firstId) setSelectedCourseId(firstId);
    }
  }, [trackGroups, standaloneCourses, completedCourses, selectedCourseId]);

  // Every course in the track is done and only the track's OWN final exam is left. Distinct from
  // a course final: it hangs off the track, not off any course, so no course tile can surface it.
  const trackFinalOutstanding = (tg: TrackGroup) =>
    tg.allCoursesCompleted && !tg.finalPassed && !!tg.finalExamId;

  const selectedCourse =
    trackGroups.flatMap((g) => g.courses).find((c) => c.course_id === selectedCourseId) ||
    standaloneCourses.find((c) => c.course_id === selectedCourseId) ||
    completedCourses.find((c) => c.course_id === selectedCourseId);

  const filteredModules = modulesWithStatus.filter((m) => m.course_id === selectedCourseId);

  const { user } = useAuth();
  const navigate = useNavigate();

  // Returning from an in-video checkpoint exam: reopen the module the student was watching so the
  // existing resume-on-load feature drops them back at the right spot — the segment start after a
  // fail (server already rolled LastWatchedPositionSeconds back), or just past the gate they passed.
  // The key is written in ModuleMedia immediately before navigating to the gate's exam.
  useEffect(() => {
    if (typeof window === "undefined" || modulesWithStatus.length === 0) return;
    const pendingModuleId = sessionStorage.getItem("pending_video_checkpoint_module");
    if (!pendingModuleId) return;
    sessionStorage.removeItem("pending_video_checkpoint_module");
    const mod = modulesWithStatus.find((m) => m.id === pendingModuleId);
    if (mod && !mod.isLocked && !(mod as any).is_locked) {
      setSelectedCourseId(mod.course_id);
      openModule(mod);
    }
  }, [modulesWithStatus]);

  const { data: certificates = [] } = useQuery({
    queryKey: STUDENT_QUERY_KEYS.userCertificates(user?.id),
    queryFn: () => getCertificates(user!.id),
    enabled: !!user?.id,
    ...STUDENT_QUERY_OPTIONS,
  });

  // Fetch course checkpoints (progression gates). Only published gates apply.
  const {
    data: allCheckpoints = [],
    isError: checkpointsError,
    refetch: refetchCheckpoints,
  } = useQuery<Checkpoint[]>({
    queryKey: STUDENT_QUERY_KEYS.courseCheckpoints(selectedCourseId),
    queryFn: () => fetchCourseCheckpoints(selectedCourseId!),
    enabled: !!selectedCourseId,
    ...STUDENT_QUERY_OPTIONS,
  });
  const checkpoints = allCheckpoints.filter((cp) => cp.is_published);

  // Fetch available exams from unified backend source of truth
  const { data: availableExams = [] } = useQuery({
    queryKey: STUDENT_QUERY_KEYS.availableExams(user?.id),
    queryFn: fetchAvailableExams,
    enabled: !!user?.id,
    ...STUDENT_QUERY_OPTIONS,
  });

  // Fetch past sessions for the checkpoints to check if they are passed
  const { data: pastSessions = {} } = useQuery({
    queryKey: ["past-exam-sessions-for-checkpoints", user?.id, checkpoints.map((c) => c.exam_id)],
    queryFn: async () => {
      if (!user?.id || checkpoints.length === 0) return {};
      const examIds = checkpoints.map((c) => c.exam_id).filter(Boolean);
      const { fetchAllPastExamSessions } = await import("@/lib/api/exams");
      return fetchAllPastExamSessions(user.id, examIds);
    },
    enabled: !!user?.id && checkpoints.length > 0,
    ...STUDENT_QUERY_OPTIONS,
  });

  // Sort modules by sequence order
  const sortedModules = [...filteredModules].sort((a, b) => a.sequence_order - b.sequence_order);

  // Module locking is server-authoritative: ask the backend whether each module
  // is unlocked (it owns all checkpoint/progression rules). We never recompute it.
  const { data: unlockStatuses = {} } = useQuery<
    Record<
      string,
      {
        is_unlocked: boolean;
        lock_reason?: string | null;
        blocking_checkpoint_name?: string | null;
        blocking_module_name?: string | null;
        blocking_course_name?: string | null;
        blocking_assignment_title?: string | null;
      }
    >
  >({
    queryKey: ["module-unlock-status", selectedCourseId, sortedModules.map((m) => m.id)],
    enabled: !!selectedCourseId && sortedModules.length > 0,
    ...STUDENT_QUERY_OPTIONS,
    queryFn: async () => {
      const moduleIds = sortedModules.map((m) => m.id);
      try {
        const statuses = await fetchModuleUnlockStatuses(moduleIds);
        return Object.fromEntries(
          moduleIds.map((id) => {
            const s = statuses[id];
            return [
              id,
              {
                is_unlocked: s?.is_unlocked ?? true,
                lock_reason: s?.lock_reason ?? null,
                blocking_checkpoint_name: s?.blocking_checkpoint_name ?? null,
                blocking_module_name: s?.blocking_module_name ?? null,
                blocking_course_name: s?.blocking_course_name ?? null,
                blocking_assignment_title: s?.blocking_assignment_title ?? null,
              },
            ];
          }),
        );
      } catch (err) {
        // On failure, don't hard-lock the UI — the backend still enforces on
        // watch/complete (TrainingController.EnforceModuleUnlockedAsync), so a failed status
        // check must not block a valid module. Logged rather than swallowed silently: this
        // path makes a locked module look unlocked until the student acts on it, so it needs
        // to be visible in the console when diagnosing "the lock state looks wrong".
        //
        // This is now all-or-nothing where it used to be per module: one request answers for
        // every module, so a failure degrades the whole page to unlocked rather than one row.
        console.warn("Module unlock-status check failed; treating as unlocked", moduleIds, err);
        return Object.fromEntries(
          moduleIds.map((id) => [
            id,
            {
              is_unlocked: true,
              lock_reason: null,
              blocking_checkpoint_name: null,
              blocking_module_name: null,
              blocking_course_name: null,
              blocking_assignment_title: null,
            },
          ]),
        );
      }
    },
  });

  // Turns the backend's lock reason into a student-facing message. A module can be gated by
  // an earlier COURSE in the track that isn't finished yet (track sequencing), by an earlier
  // module that isn't finished yet (sequential progression), by an unpassed checkpoint exam, or by
  // an assignment the student has not handed in yet.
  const describeLock = (unlock?: {
    lock_reason?: string | null;
    blocking_checkpoint_name?: string | null;
    blocking_module_name?: string | null;
    blocking_course_name?: string | null;
    blocking_assignment_title?: string | null;
  }) => {
    if (unlock?.lock_reason === "previous_course_incomplete") {
      return unlock.blocking_course_name
        ? `Locked until you complete the previous course "${unlock.blocking_course_name}"`
        : "Complete the previous course in this track to unlock";
    }
    if (unlock?.lock_reason === "previous_module_incomplete") {
      return unlock.blocking_module_name
        ? `Locked until you complete "${unlock.blocking_module_name}"`
        : "Complete the previous module to unlock";
    }
    // Being ACCEPTED is what opens it, not merely submitting. Worth spelling out: a student who has
    // handed work in and is waiting on a review is still blocked, and would otherwise read this as
    // a bug rather than as the queue.
    if (unlock?.lock_reason === "assignment_not_completed") {
      return unlock.blocking_assignment_title
        ? `Locked until your assignment "${unlock.blocking_assignment_title}" is reviewed and accepted`
        : "Your assignment for the earlier module must be accepted to unlock";
    }
    return unlock?.blocking_checkpoint_name
      ? `Locked until "${unlock.blocking_checkpoint_name}" is passed`
      : "Complete the checkpoint below to unlock";
  };

  // Position of each module in the ordered timeline — used to expand a checkpoint's
  // [start_module_id, end_module_id] range into the concrete set of covered module ids.
  const moduleIndexById = new Map(sortedModules.map((m, i) => [m.id, i] as const));
  const coveredModuleIds = (cp: Checkpoint): Set<string> => {
    if (!cp.start_module_id || !cp.end_module_id) return new Set();
    const s = moduleIndexById.get(cp.start_module_id);
    const e = moduleIndexById.get(cp.end_module_id);
    if (s == null || e == null) return new Set();
    const lo = Math.min(s, e);
    const hi = Math.max(s, e);
    return new Set(sortedModules.slice(lo, hi + 1).map((m) => m.id));
  };

  // A checkpoint is in the REMEDIAL state when its exam is locked specifically because a prior
  // attempt failed (backend-authoritative flag on the available-exam record). Its still-incomplete
  // covered modules must be shown as "rewatch required", distinct from an ordinary "not reached yet"
  // lock. (This is a checkpoint-failure remedial, carried on the progress record.)
  const remedialCheckpointNameByModule = new Map<string, string>();
  checkpoints.forEach((cp) => {
    const ae = availableExams.find((e) => e.exam_id === cp.exam_id);
    if (!ae?.requires_remedial_rewatch) return;
    for (const mid of coveredModuleIds(cp)) {
      const mod = sortedModules.find((m) => m.id === mid);
      if (mod && !mod.isCompleted) remedialCheckpointNameByModule.set(mid, cp.name);
    }
  });

  const modulesWithCheckpointLocks = sortedModules.map((module) => {
    const unlock = unlockStatuses[module.id];
    const backendLocked = unlock ? !unlock.is_unlocked : false;
    const locked = module.isLocked || backendLocked;
    const remedialCheckpointName = remedialCheckpointNameByModule.get(module.id);
    // Disambiguated: remedial styling only for a genuine rewatch requirement (a failed-checkpoint
    // range) — NOT for an ordinary sequential lock.
    const isRemedialLocked = module.isRemedialLocked || !!remedialCheckpointName;
    return {
      ...module,
      isLocked: locked,
      isRemedialLocked,
      checkpointLockReason: remedialCheckpointName
        ? `You didn't pass "${remedialCheckpointName}". Rewatch this video, then retake the checkpoint.`
        : backendLocked
          ? describeLock(unlock)
          : locked
            ? "Complete previous requirements"
            : null,
    };
  });

  // A checkpoint's exam readiness / passed / remedial state is display-only; the backend
  // owns actual gating. Readiness comes from exam eligibility, passed from sessions.
  const makeCheckpointItem = (cp: Checkpoint) => {
    const eligibility = availableExams.find((ae) => ae.exam_id === cp.exam_id);
    const isUnlocked = eligibility ? eligibility.is_ready : false;
    const isRemedial = !!eligibility?.requires_remedial_rewatch;
    // Distinguished from the two other locks because the remedy is completely different: there is
    // nothing the student can watch or finish to clear it, only a Team Leader can. Rendering it as
    // "Complete Modules Above" sent them back to videos they had already finished. Now reachable in
    // normal use — a checkpoint attempt terminated for proctoring violations lands here by design.
    const needsStaffReopen =
      !isRemedial && !isUnlocked && eligibility?.lock_reason_code === "AttemptsExhausted";
    const pastRes = pastSessions[cp.exam_id];
    const isPassed = pastRes?.passed === true;
    // A pass this student EARNED and staff then voided by authorizing a new attempt over it. It is
    // the difference between "you haven't passed this yet" and "you passed this and it was reset" —
    // indistinguishable from `isPassed` alone, which is false in both cases because it answers the
    // gating question. Without it the student sees a gate they have to sit again sitting directly
    // above their own 100% in their exam history, with nothing anywhere to explain why.
    const supersededPassAt = pastRes?.superseded_pass_at ?? null;
    const supersededPassReason = pastRes?.superseded_pass_reason ?? null;
    return {
      type: "checkpoint" as const,
      data: cp,
      isUnlocked,
      isPassed,
      isRemedial,
      needsStaffReopen,
      supersededPassAt,
      supersededPassReason,
    };
  };

  // Build the interleaved render list. A checkpoint is placed AFTER the last module of its
  // covered range (end_module_id); a rangeless/entrance checkpoint renders at the course start.
  const renderList: (
    | { type: "module"; data: (typeof modulesWithCheckpointLocks)[0] }
    | {
        type: "checkpoint";
        data: Checkpoint;
        isUnlocked: boolean;
        isPassed: boolean;
        isRemedial: boolean;
        needsStaffReopen: boolean;
        supersededPassAt: string | null;
        supersededPassReason: string | null;
      }
  )[] = [];

  checkpoints
    .filter((cp) => !cp.end_module_id)
    .forEach((cp) => renderList.push(makeCheckpointItem(cp)));

  modulesWithCheckpointLocks.forEach((module) => {
    renderList.push({ type: "module", data: module });
    checkpoints
      .filter((cp) => cp.end_module_id === module.id)
      .forEach((cp) => renderList.push(makeCheckpointItem(cp)));
  });

  if (isLoading)
    return (
      <div className="p-8 max-w-6xl mx-auto">
        <CardSkeleton count={4} className="sm:grid-cols-2" />
      </div>
    );

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8">
      {/* Learning Tracks & Standalone Courses Selector */}
      {(trackGroups.length > 0 || standaloneCourses.length > 0 || completedCourses.length > 0) && (
        <div className="space-y-6">
          {trackGroups.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold tracking-tight flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-primary" />
                Assigned Learning Tracks
              </h2>
              <div className="grid gap-6 md:grid-cols-2">
                {trackGroups.map((tg) => (
                  <Card
                    key={tg.track_id}
                    className="relative overflow-hidden border bg-card shadow-sm"
                  >
                    <div className="absolute top-0 left-0 w-1.5 h-full bg-primary" />
                    <CardHeader className="pb-3 pl-6">
                      <div className="flex justify-between items-start gap-2">
                        <CardTitle className="text-lg font-bold">{tg.track_name}</CardTitle>
                        {/* Gated on tg.isCompleted, NOT avgProgress: the progress bar is
                            module-based, so a track whose lessons are all watched hits 100% while
                            its course finals / track final are still unpassed. Reading the bar as
                            completion labelled such a track "Completed" and told the student they
                            were done with exams outstanding. */}
                        <Badge
                          variant={tg.isCompleted ? "default" : "outline"}
                          className={
                            tg.isCompleted
                              ? "bg-green-500/10 text-green-600 border-green-500/20"
                              : trackFinalOutstanding(tg)
                                ? "bg-amber-500/10 text-amber-600 border-amber-500/20"
                                : ""
                          }
                        >
                          {tg.isCompleted
                            ? "Completed"
                            : trackFinalOutstanding(tg)
                              ? "Final Exam Left"
                              : "In Progress"}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4 pl-6">
                      {/* Overall track progress */}
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs text-muted-foreground font-semibold">
                          <span>Path Progress</span>
                          <span>{tg.avgProgress}%</span>
                        </div>
                        <Progress value={tg.avgProgress} className="h-2 w-full" />
                      </div>

                      {/* Every course done but the track's own final exam still unpassed. Without
                          this the card was a dead end: 100% progress, "In Progress", and no hint
                          that a track-level exam existed or where to sit it — the student had to
                          find it on the Exams page by themselves. */}
                      {trackFinalOutstanding(tg) && (
                        <Button
                          size="sm"
                          className="w-full text-xs font-semibold bg-amber-500 hover:bg-amber-600 text-white flex items-center justify-center gap-1 py-1.5"
                          onClick={() =>
                            navigate({ to: "/exams", search: { examId: tg.finalExamId! } })
                          }
                        >
                          <ShieldAlert className="h-3.5 w-3.5" />
                          Complete Track Final Exam to Finish
                        </Button>
                      )}

                      {/* Course list under track */}
                      <div className="space-y-2 border-t pt-3 mt-1">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                          Course Roadmap (Click to select)
                        </p>
                        <div className="space-y-1.5">
                          {tg.courses.map((course, idx) => {
                            const isSelected = selectedCourseId === course.course_id;

                            // Server-authoritative: the backend decides track-sequencing locks and
                            // returns is_locked on the course tile. The client only displays it.
                            const isLocked = !!course.is_locked;

                            const examEligibility = availableExams.find(
                              (ae) => ae.exam_id === course.final_exam_id,
                            );
                            const isExamReady = examEligibility
                              ? examEligibility.is_ready && !examEligibility.already_passed
                              : false;

                            // `completion_pct` counts modules only; `is_completed` is the server's
                            // real verdict (modules AND checkpoints AND the final exam). Treating
                            // 100% as "Completed" marked a course done while its final was still
                            // pending — and it fell through to that label whenever the exam was
                            // missing from availableExams (awaiting grading, window closed), so
                            // isExamReady couldn't catch it either.
                            const isCourseCompleted = !!course.is_completed;
                            const examOutstanding =
                              (course.completion_pct ?? 0) >= 100 && !isCourseCompleted;
                            return (
                              <button
                                key={course.course_id}
                                onClick={() => {
                                  if (isLocked) {
                                    toast.error(
                                      "Previous courses must be completed first in order to be able to open this course.",
                                    );
                                    return;
                                  }
                                  setSelectedCourseId(course.course_id);
                                }}
                                className={`w-full flex items-center justify-between text-xs py-2 px-3 rounded-lg border transition-all text-left ${
                                  isLocked
                                    ? "opacity-50 cursor-not-allowed hover:bg-transparent text-muted-foreground"
                                    : isSelected
                                      ? "border-primary bg-primary/10 text-primary font-bold shadow-sm"
                                      : "border-transparent hover:bg-muted/60 text-foreground"
                                }`}
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="font-semibold text-muted-foreground shrink-0">
                                    {idx + 1}.
                                  </span>
                                  <span className="truncate flex items-center gap-1.5">
                                    {course.course_title}
                                    {isLocked && (
                                      <Lock className="h-3 w-3 text-muted-foreground shrink-0" />
                                    )}
                                  </span>
                                </div>
                                <Badge
                                  variant={
                                    isLocked
                                      ? "outline"
                                      : isCourseCompleted
                                        ? "default"
                                        : isExamReady || examOutstanding
                                          ? "outline"
                                          : (course.completion_pct ?? 0) > 0
                                            ? "secondary"
                                            : "outline"
                                  }
                                  className={`text-[10px] scale-95 origin-right shrink-0 ${
                                    isLocked
                                      ? "bg-muted text-muted-foreground border-muted-foreground/20"
                                      : !isCourseCompleted && (isExamReady || examOutstanding)
                                        ? "bg-amber-500/10 text-amber-600 border-amber-500/20"
                                        : ""
                                  }`}
                                >
                                  {isLocked
                                    ? "Locked"
                                    : isCourseCompleted
                                      ? "Completed"
                                      : isExamReady
                                        ? "Exam Ready"
                                        : examOutstanding
                                          ? "Exam Pending"
                                          : (course.completion_pct ?? 0) > 0
                                            ? `${course.completion_pct ?? 0}%`
                                            : "Not Started"}
                                </Badge>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {standaloneCourses.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold tracking-tight flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-primary" />
                Standalone Courses (Click to select)
              </h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {standaloneCourses.map((c) => {
                  const isSelected = selectedCourseId === c.course_id;
                  const examEligibility = availableExams.find(
                    (ae) => ae.exam_id === c.final_exam_id,
                  );
                  const isExamReady = examEligibility
                    ? examEligibility.is_ready && !examEligibility.already_passed
                    : false;
                  // Same rule as the track roadmap badge: is_completed is the server's verdict,
                  // completion_pct is module progress only.
                  const isCourseCompleted = !!c.is_completed;
                  const examOutstanding = (c.completion_pct ?? 0) >= 100 && !isCourseCompleted;
                  return (
                    <Card
                      key={c.course_id}
                      className={`cursor-pointer transition-all border hover:border-primary/50 relative overflow-hidden ${isSelected ? "ring-2 ring-primary border-transparent bg-primary/5" : ""}`}
                      onClick={() => setSelectedCourseId(c.course_id)}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex justify-between items-start gap-2">
                          <CardTitle className="text-sm font-bold truncate">
                            {c.course_title}
                          </CardTitle>
                          <Badge
                            variant={isCourseCompleted ? "default" : "outline"}
                            className={
                              isCourseCompleted
                                ? "bg-green-500/10 text-green-600 border-green-500/20 shrink-0"
                                : isExamReady || examOutstanding
                                  ? "bg-amber-500/10 text-amber-600 border-amber-500/20 shrink-0"
                                  : "shrink-0"
                            }
                          >
                            {isCourseCompleted
                              ? "Completed"
                              : isExamReady
                                ? "Exam Ready"
                                : examOutstanding
                                  ? "Exam Pending"
                                  : `${c.completion_pct ?? 0}%`}
                          </Badge>
                        </div>
                        {isExamReady && (
                          <div className="mt-2.5">
                            <Button
                              size="sm"
                              className="w-full text-xs font-semibold bg-amber-500 hover:bg-amber-600 text-white flex items-center justify-center gap-1 py-1.5"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate({
                                  to: "/exams",
                                  search: { examId: c.final_exam_id },
                                });
                              }}
                            >
                              <ShieldAlert className="h-3.5 w-3.5" />
                              Complete Final Exam to Finish
                            </Button>
                          </div>
                        )}
                      </CardHeader>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {completedCourses.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold tracking-tight flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                Completed Courses (Click to select for review)
              </h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {completedCourses.map((c) => {
                  const isSelected = selectedCourseId === c.course_id;
                  const cert = certificates.find((cert: any) => cert.course?.id === c.course_id);
                  return (
                    <Card
                      key={c.course_id}
                      className={`cursor-pointer transition-all border hover:border-green-500/50 relative overflow-hidden ${isSelected ? "ring-2 ring-green-500 border-transparent bg-green-500/[0.02]" : ""}`}
                      onClick={() => setSelectedCourseId(c.course_id)}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex justify-between items-start gap-2">
                          <CardTitle className="text-sm font-bold truncate">
                            {c.course_title}
                          </CardTitle>
                          <Badge
                            variant="default"
                            className="bg-green-500/10 text-green-600 border-green-500/20 shrink-0"
                          >
                            Completed
                          </Badge>
                        </div>
                        {cert && (
                          <div className="mt-2">
                            {cert.status === "issued" ? (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-[10px] gap-1 px-2.5 py-1 h-auto text-primary border-primary/20 hover:bg-primary/5"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(`/verify/${cert.cert_code}`, "_blank");
                                }}
                              >
                                <Award className="h-3 w-3" />
                                View Certificate
                              </Button>
                            ) : cert.status === "pending" || cert.status === "generating" ? (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-[10px] gap-1 px-2.5 py-1 h-auto text-muted-foreground border-muted/20"
                                disabled
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Loader2 className="h-3 w-3 animate-spin" />
                                Generating...
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-[10px] gap-1 px-2.5 py-1 h-auto text-destructive border-destructive/20 hover:bg-destructive/5"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(`/verify/${cert.cert_code}`, "_blank");
                                }}
                              >
                                <AlertTriangle className="h-3 w-3" />
                                Cert: {cert.status}
                              </Button>
                            )}
                          </div>
                        )}
                      </CardHeader>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modules List for Selected Course */}
      {selectedCourse ? (
        <div className="space-y-4 pt-4 border-t">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
              <GraduationCap className="h-6 w-6 text-primary" />
              {selectedCourse.course_title} - Modules
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              {selectedCourse.course_description ||
                "Complete the lessons below in sequential order."}
            </p>
          </div>

          {selectedCourse?.is_completed && (
            <div className="rounded-xl border border-green-200 bg-green-500/10 px-4 py-3 dark:border-green-900/30 flex items-center gap-2.5 text-green-700 dark:text-green-300">
              <CheckCircle className="h-5 w-5 text-green-500 shrink-0" />
              <div className="text-xs">
                <span className="font-semibold">Review Mode</span> — You have completed this course.
                Feel free to re-watch modules and review material.
              </div>
            </div>
          )}

          {/* Distinguish a failed checkpoint fetch from "no checkpoints configured" — otherwise a
              broken request silently looks identical to a course that simply has no gates. */}
          {checkpointsError && (
            <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 text-destructive">
              <div className="flex items-center gap-2.5 text-xs">
                <AlertTriangle className="h-5 w-5 shrink-0" />
                <span>
                  Couldn't load this course's checkpoints. The timeline may be incomplete.
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs border-destructive/40 text-destructive hover:bg-destructive/10 self-start sm:self-auto"
                onClick={() => refetchCheckpoints()}
              >
                Retry
              </Button>
            </div>
          )}

          {selectedCourse &&
            selectedCourse.final_exam_id &&
            (selectedCourse.completion_pct ?? 0) >= 100 &&
            !selectedCourse.is_completed && (
              <div className="rounded-xl border border-amber-200 bg-amber-500/10 px-4 py-3.5 dark:border-amber-900/30 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-amber-700 dark:text-amber-300">
                <div className="flex items-center gap-2.5">
                  <ShieldAlert className="h-5 w-5 text-amber-500 shrink-0" />
                  <div className="text-xs">
                    <span className="font-bold">Final Exam Available</span> — You have completed all
                    training modules. Take the final exam to complete this course and earn your
                    certificate.
                  </div>
                </div>
                <Button
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-500 text-white font-semibold text-xs shrink-0 self-start sm:self-auto flex items-center gap-1"
                  onClick={() =>
                    navigate({
                      to: "/exams",
                      search: { examId: selectedCourse.final_exam_id },
                    })
                  }
                >
                  Start Final Exam
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}

          {renderList.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground bg-muted/20 border border-dashed rounded-xl">
              No training modules found in this course.
            </div>
          ) : (
            /* ── Sequential progression timeline ──────────────────────────────
               Checkpoints strictly gate the modules that follow them, so the path
               is rendered as ONE vertical timeline (Module ↓ Checkpoint ↓ Module),
               never a wrapped grid. A continuous rail on the left, with a state
               node per step, makes the ordering and the locked/unlocked gate path
               unambiguous — the student is guided straight downwards. */
            <div className="relative">
              {renderList.map((item, index) => {
                const isLast = index === renderList.length - 1;

                if (item.type === "module") {
                  const module = item.data;
                  // Step number = position among modules only (checkpoints don't count).
                  const moduleStepNo = renderList
                    .slice(0, index + 1)
                    .filter((it) => it.type === "module").length;
                  const nodeState = module.isCompleted
                    ? "done"
                    : module.isLocked
                      ? "locked"
                      : "active";

                  return (
                    <div key={module.id} className="relative flex gap-4 sm:gap-5 pb-5 last:pb-0">
                      {/* Rail: state node + connector line to the next step */}
                      <div className="flex flex-col items-center shrink-0">
                        <div
                          className={`flex h-11 w-11 items-center justify-center rounded-full border-2 shrink-0 transition-colors ${
                            nodeState === "done"
                              ? "bg-green-500 border-green-500 text-white"
                              : nodeState === "locked"
                                ? "bg-muted border-muted-foreground/20 text-muted-foreground"
                                : "bg-primary border-primary text-primary-foreground"
                          }`}
                        >
                          {nodeState === "done" ? (
                            <CheckCircle className="h-5 w-5" />
                          ) : nodeState === "locked" ? (
                            <Lock className="h-4 w-4" />
                          ) : (
                            <span className="text-sm font-bold">{moduleStepNo}</span>
                          )}
                        </div>
                        {!isLast && (
                          <div
                            className={`w-0.5 flex-1 min-h-[2rem] mt-1 ${module.isCompleted ? "bg-green-500/40" : "bg-border"}`}
                          />
                        )}
                      </div>

                      {/* Module card (full-width timeline row) */}
                      <Card
                        className={`flex-1 relative overflow-hidden transition-all hover:shadow-md mb-1 ${module.isLocked ? "opacity-70" : ""}`}
                      >
                        {module.isRemedialLocked ? (
                          <div
                            className={`absolute inset-0 z-10 flex flex-col items-center justify-center p-4 text-center pointer-events-none ${
                              module.isLocked
                                ? "bg-background/60 backdrop-blur-[2px]"
                                : "bg-destructive/5"
                            }`}
                          >
                            <Lock className="h-7 w-7 text-destructive mb-1.5" />
                            <span className="text-sm font-bold text-destructive">
                              Rewatch Required
                            </span>
                            <span className="text-xs text-muted-foreground mt-0.5 max-w-[240px]">
                              {module.checkpointLockReason
                                ? module.checkpointLockReason
                                : module.isLocked
                                  ? "Rewatch the earlier videos first."
                                  : "Click to rewatch this video."}
                            </span>
                          </div>
                        ) : (
                          module.isLocked && (
                            <div className="absolute inset-0 z-10 bg-background/55 backdrop-blur-[1.5px] flex flex-col items-center justify-center p-4 text-center">
                              <Lock className="h-7 w-7 text-muted-foreground/80 mb-1.5" />
                              <span className="text-xs font-semibold text-muted-foreground/90">
                                {module.checkpointLockReason || "Module Locked"}
                              </span>
                              <span className="text-[10px] text-muted-foreground/60 mt-0.5">
                                {module.checkpointLockReason
                                  ? "Pass the checkpoint above to unlock"
                                  : "Complete previous modules first"}
                              </span>
                            </div>
                          )
                        )}

                        <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-5">
                          <div className="flex-1 min-w-0 space-y-1.5">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                                Module {moduleStepNo}
                              </span>
                              {module.isCompleted ? (
                                <Badge className="bg-green-500/10 text-green-600 border-green-500/20 text-[10px] scale-95 origin-left">
                                  Completed
                                </Badge>
                              ) : module.isLocked ? (
                                <Badge
                                  variant="outline"
                                  className="text-[10px] scale-95 origin-left"
                                >
                                  Locked
                                </Badge>
                              ) : (
                                <Badge
                                  variant="secondary"
                                  className="text-[10px] scale-95 origin-left"
                                >
                                  {(module.progressPercent ?? 0) > 0
                                    ? "In Progress"
                                    : "Not Started"}
                                </Badge>
                              )}
                            </div>
                            <h3 className="text-base font-bold leading-snug truncate flex items-center gap-2">
                              {module.title}
                              {module.isCompleted && (
                                <CheckCircle className="text-green-500 h-4 w-4 shrink-0" />
                              )}
                            </h3>
                            <p className="text-xs text-muted-foreground line-clamp-2">
                              {module.description}
                            </p>
                            <div className="flex items-center gap-2 pt-1">
                              <Progress
                                value={module.progressPercent}
                                className="h-1.5 flex-1 max-w-[220px]"
                              />
                              <span className="text-[11px] text-muted-foreground shrink-0">
                                {module.progressPercent}%
                              </span>
                            </div>
                          </div>
                          <div className="shrink-0">
                            <Button
                              className="w-full sm:w-auto"
                              disabled={module.isLocked}
                              variant={module.isCompleted ? "outline" : "default"}
                              onClick={() => openModule(module)}
                            >
                              {module.isCompleted ? "Review Material" : "Start Module"}
                              {!module.isCompleted && <PlayCircle className="ml-2 h-4 w-4" />}
                            </Button>
                          </div>
                        </div>
                      </Card>
                    </div>
                  );
                }

                // ── Checkpoint step ─────────────────────────────────────────
                const checkpoint = item.data;
                const cpState = item.isPassed ? "passed" : item.isUnlocked ? "ready" : "locked";

                return (
                  <div key={checkpoint.id} className="relative flex gap-4 sm:gap-5 pb-5 last:pb-0">
                    {/* Rail: gate node + connector line */}
                    <div className="flex flex-col items-center shrink-0">
                      <div
                        className={`flex h-11 w-11 items-center justify-center rounded-full border-2 shrink-0 transition-colors ${
                          cpState === "passed"
                            ? "bg-green-500 border-green-500 text-white"
                            : cpState === "ready"
                              ? "bg-amber-500 border-amber-500 text-white"
                              : "bg-muted border-muted-foreground/20 text-muted-foreground"
                        }`}
                      >
                        {cpState === "passed" ? (
                          <CheckCircle className="h-5 w-5" />
                        ) : cpState === "locked" ? (
                          <Lock className="h-4 w-4" />
                        ) : (
                          <ShieldAlert className="h-5 w-5" />
                        )}
                      </div>
                      {!isLast && (
                        <div
                          className={`w-0.5 flex-1 min-h-[2rem] mt-1 ${item.isPassed ? "bg-green-500/40" : "bg-border"}`}
                        />
                      )}
                    </div>

                    {/* Checkpoint gate card (full-width timeline row) */}
                    <Card
                      className={`flex-1 relative overflow-hidden transition-all hover:shadow-lg border-2 mb-1 ${
                        cpState === "passed"
                          ? "border-green-500/25 bg-green-500/[0.02]"
                          : cpState === "ready"
                            ? "border-amber-500/40 bg-amber-500/[0.03] shadow-[0_0_18px_rgba(245,158,11,0.06)]"
                            : "border-dashed border-muted-foreground/25 bg-muted/[0.03] opacity-80"
                      }`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-5">
                        <div className="flex-1 min-w-0 space-y-1.5">
                          <span
                            className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider ${
                              cpState === "passed"
                                ? "text-green-600"
                                : cpState === "ready"
                                  ? "text-amber-600"
                                  : "text-muted-foreground"
                            }`}
                          >
                            <ShieldAlert className="h-3 w-3" />
                            Checkpoint Gate
                          </span>
                          <h3 className="text-base font-bold leading-snug truncate">
                            {checkpoint.name}
                          </h3>
                          {item.isRemedial && !item.isPassed ? (
                            // Name the lesson to resume from when the server tells us which one
                            // (next_required_module is the first still-incomplete lesson in the
                            // reset range). "Rewatch the videos above" alone left the student
                            // guessing which of them they had to redo.
                            <p className="text-xs text-destructive font-medium line-clamp-2">
                              {(() => {
                                const next = availableExams.find(
                                  (ae) => ae.exam_id === checkpoint.exam_id,
                                )?.next_required_module;
                                return next
                                  ? `You didn't pass. Rewatch "${next}" (and any lessons after it), then retake this checkpoint.`
                                  : "You didn't pass. Rewatch the videos above, then retake this checkpoint.";
                              })()}
                            </p>
                          ) : !item.isPassed && item.supersededPassAt ? (
                            // The student DID pass this and a team leader reset it. Ranked above the
                            // generic line because without it the gate is unexplainable from the
                            // student's side: their own exam history still shows the score they got,
                            // the modules behind this card are locked, and every other label on the
                            // page reads as though they had simply never sat it.
                            <p className="text-xs text-amber-600 font-medium">
                              {/* This date is when the pass was RESET, not when it was earned —
                                  the field carries ExamSession.ProgressionSupersededAt. Worded as
                                  "you passed on <date>" it told the student they had sat the exam on
                                  a day they hadn't, which reads as the system having lost track of
                                  their attempt. The sitting's own date is in their exam history. */}
                              You passed this checkpoint earlier, but on{" "}
                              {new Date(item.supersededPassAt).toLocaleDateString()} your team leader
                              reset it and gave you a new attempt — so it needs passing again to
                              reopen the lessons below.
                              {item.supersededPassReason
                                ? ` Reason: ${item.supersededPassReason}`
                                : ""}
                            </p>
                          ) : (
                            <p className="text-xs text-muted-foreground line-clamp-2">
                              {checkpoint.exam_title ||
                                "Pass this exam to unlock the modules below."}
                            </p>
                          )}
                          {(() => {
                            // Surface the checkpoint exam's availability window (mirrors the student
                            // exams page). Uses the same availableExams data already fetched here.
                            const cpExam = availableExams.find(
                              (ae) => ae.exam_id === checkpoint.exam_id,
                            );
                            if (!cpExam?.start_date && !cpExam?.end_date) return null;
                            return (
                              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground pt-0.5">
                                {cpExam?.start_date && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" /> Opens{" "}
                                    {new Date(cpExam.start_date).toLocaleString()}
                                  </span>
                                )}
                                {cpExam?.end_date && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" /> Closes{" "}
                                    {new Date(cpExam.end_date).toLocaleString()}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                        <div className="flex flex-col items-stretch sm:items-end gap-2 shrink-0">
                          {item.isPassed ? (
                            <Badge className="bg-green-500/10 text-green-600 border-green-500/25 self-start sm:self-end">
                              Passed
                            </Badge>
                          ) : item.isRemedial ? (
                            <Badge
                              variant="outline"
                              className="border-destructive/40 text-destructive self-start sm:self-end"
                            >
                              Rewatch Required
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className={
                                item.isUnlocked
                                  ? "border-amber-500/30 text-amber-600 self-start sm:self-end"
                                  : "self-start sm:self-end"
                              }
                            >
                              {item.isUnlocked
                                ? // "Ready" is true but useless to a student who already cleared
                                  // this gate once — it reads as a checkpoint they never reached.
                                  item.supersededPassAt
                                  ? "Retake Required"
                                  : "Ready"
                                : item.needsStaffReopen
                                  ? "Reopen Required"
                                  : "Locked"}
                            </Badge>
                          )}
                          <Button
                            className="w-full sm:w-auto font-semibold transition-all"
                            variant={
                              item.isPassed ? "outline" : item.isUnlocked ? "default" : "secondary"
                            }
                            disabled={!item.isUnlocked && !item.isPassed}
                            onClick={() => {
                              if (item.isPassed) {
                                toast.info("You have already passed this checkpoint exam.");
                                return;
                              }
                              navigate({ to: "/exams", search: { examId: checkpoint.exam_id } });
                            }}
                          >
                            {item.isPassed ? (
                              "Checkpoint Passed"
                            ) : item.isUnlocked ? (
                              <>
                                Take Checkpoint Exam
                                <ArrowRight className="ml-2 h-4 w-4" />
                              </>
                            ) : item.isRemedial ? (
                              "Rewatch Videos Above"
                            ) : item.needsStaffReopen ? (
                              "Ask Your Team Leader to Reopen"
                            ) : (
                              "Complete Modules Above"
                            )}
                          </Button>
                        </div>
                      </div>
                    </Card>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        trackGroups.length === 0 &&
        standaloneCourses.length === 0 && (
          <div className="p-12 text-center text-muted-foreground bg-muted/20 border border-dashed rounded-2xl max-w-xl mx-auto space-y-3">
            <BookOpen className="h-12 w-12 text-muted-foreground/60 mx-auto" />
            <h3 className="text-lg font-bold text-foreground">No Courses Assigned</h3>
            <p className="text-sm">
              You are not currently enrolled in any learning tracks or courses. Please contact your
              coordinator to get started.
            </p>
          </div>
        )
      )}

      {/* ── Video Player Dialog ── */}
      <Dialog
        open={!!selectedModule}
        onOpenChange={(open) => {
          if (!open) void closeModule();
        }}
      >
        {/* Wider + fully dark, no internal padding — we control spacing manually */}
        <DialogContent className="max-w-5xl p-0 overflow-hidden bg-[#0a0a0f] border border-white/8 shadow-[0_32px_80px_rgba(0,0,0,0.8)] rounded-2xl">
          {/* ── Header ── */}
          <div className="relative px-4 sm:px-7 pt-6 pb-4 flex items-start justify-between gap-4 border-b border-white/6">
            {/* Accent glow behind title */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-transparent pointer-events-none" />
            <div className="relative min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-[0.15em] text-blue-400/70 font-semibold">
                  Training Module
                </span>
              </div>
              <DialogTitle className="text-xl font-bold text-white leading-tight break-words">
                {selectedModule?.title}
              </DialogTitle>
            </div>
            {/* Progress pill in header */}
            <div className="relative shrink-0 mt-1">
              <span
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${
                  videoProgress >= 99.9
                    ? "bg-green-500/15 text-green-400 border-green-500/30"
                    : "bg-white/5 text-white/50 border-white/10"
                }`}
              >
                {videoProgress >= 99.9 ? (
                  <>
                    <CheckCircle className="h-3 w-3" /> Watched
                  </>
                ) : (
                  <>{Math.round(videoProgress)}% watched</>
                )}
              </span>
            </div>
          </div>

          {/* ── External resource: its own panel, no player ── */}
          {selectedModule?.content_type === "external_resource" ? (
            <ExternalResourcePanel
              module={selectedModule}
              onOpen={() => {
                markResourceOpened(selectedModule.id);
                window.open(
                  selectedModule.external_url!,
                  selectedModule.open_in_new_tab === false ? "_self" : "_blank",
                  "noopener,noreferrer",
                );
              }}
            />
          ) : (
            /* ── Video ── */
            selectedModule && (
              <div className="video-stage w-full">
                <ModuleMedia
                  key={selectedModule.id}
                  module={selectedModule}
                  serverResumeSeconds={serverResumeSeconds}
                  userId={user?.id}
                  setVideoProgress={setVideoProgress}
                  setIsPlaying={setIsPlaying}
                  handleVideoEnd={handleVideoEnd}
                  setActiveVideoDuration={setActiveVideoDuration}
                  handleTimeUpdate={handleTimeUpdate}
                  registerSeekTo={registerSeekTo}
                  registerPause={registerPause}
                  registerPlay={registerPlay}
                  onUnplayable={handleUnplayable}
                  onResourceOpened={markResourceOpened}
                />
              </div>
            )
          )}

          {/* ── Bottom action bar ── */}
          <div className="px-4 sm:px-7 py-5 flex flex-col sm:flex-row sm:items-center gap-4 bg-[#0d0d14] border-t border-white/6">
            {/* Left: status info */}
            <div className="flex-1 min-w-0">
              {selectedModule?.content_type === "external_resource" ? (
                selectedModule?.isCompleted ? (
                  <p className="text-xs text-white/40">You've completed this resource.</p>
                ) : (
                  <p className="text-xs text-white/40">
                    Open the resource, then mark it completed when you're done.
                  </p>
                )
              ) : selectedModule?.content_type === "document" ? (
                <p className="text-xs text-white/40">Review the material, then mark it complete.</p>
              ) : selectedModule?.requires_manual_completion ? (
                selectedModule?.isCompleted ? (
                  <p className="text-xs text-white/40">You've completed this module.</p>
                ) : (
                  <p className="text-xs text-white/40">
                    Watch the video on YouTube, then mark this module complete.
                  </p>
                )
              ) : selectedModule?.isCompleted ? (
                <p className="text-xs text-white/40">You've completed this module.</p>
              ) : (
                <p className="text-xs text-white/40">
                  Finish watching the video to complete this module.
                </p>
              )}
            </div>

            {/* Right: action buttons */}
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="ghost"
                onClick={() => void closeModule()}
                className="text-white/50 hover:text-white hover:bg-white/8 border border-white/10 h-9 px-4"
              >
                Close
              </Button>

              {/* ExternalResource completes manually, exactly like a document module — it routes
                  through the SAME completeCurrentModule mutation, so progress, track/course
                  rollups, notifications, analytics and checkpoints all fire unchanged. */}
              {selectedModule?.content_type === "external_resource" &&
                (selectedModule?.isCompleted ? (
                  <Button
                    disabled
                    className="h-9 px-5 bg-green-600/20 text-green-500/50 border border-green-500/20 font-medium"
                  >
                    <CheckCircle className="h-4 w-4 mr-1.5" />
                    Completed
                  </Button>
                ) : (
                  <Button
                    onClick={completeCurrentModule}
                    className="h-9 px-5 bg-green-600 hover:bg-green-500 text-white font-medium shadow-lg shadow-green-900/30"
                  >
                    <CheckCircle className="h-4 w-4 mr-1.5" />
                    Mark Resource as Completed
                  </Button>
                ))}

              {/* A YouTube video the owner blocked from embedding completes the same way, through
                  the SAME mutation — the server has already waived its watch-through requirement
                  (its question gates still apply). Excludes external_resource so the two branches
                  can't both render a button. */}
              {selectedModule?.requires_manual_completion &&
                selectedModule?.content_type !== "external_resource" &&
                (selectedModule?.isCompleted ? (
                  <Button
                    disabled
                    className="h-9 px-5 bg-green-600/20 text-green-500/50 border border-green-500/20 font-medium"
                  >
                    <CheckCircle className="h-4 w-4 mr-1.5" />
                    Completed
                  </Button>
                ) : (
                  <Button
                    onClick={completeCurrentModule}
                    className="h-9 px-5 bg-green-600 hover:bg-green-500 text-white font-medium shadow-lg shadow-green-900/30"
                  >
                    <CheckCircle className="h-4 w-4 mr-1.5" />
                    I've Watched It — Complete
                  </Button>
                ))}

              {selectedModule?.content_type === "document" &&
                (selectedModule?.isCompleted ? (
                  <Button
                    disabled
                    className="h-9 px-5 bg-green-600/20 text-green-500/50 border border-green-500/20 font-medium"
                  >
                    <CheckCircle className="h-4 w-4 mr-1.5" />
                    Completed
                  </Button>
                ) : (
                  <Button
                    onClick={completeCurrentModule}
                    className="h-9 px-5 bg-green-600 hover:bg-green-500 text-white font-medium shadow-lg shadow-green-900/30"
                  >
                    <CheckCircle className="h-4 w-4 mr-1.5" />
                    Mark as Complete
                  </Button>
                ))}
            </div>
          </div>

          {/* Hidden but required for accessibility */}
          <DialogDescription className="sr-only">
            Training module video player for {selectedModule?.title}
          </DialogDescription>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Student-facing panel for a ModuleType.ExternalResource module.
 *
 * Renders provider / description / estimated duration and the "Open Resource" action. There is
 * deliberately no player and no watch telemetry: the resource lives off-platform, so completion
 * is the explicit "Mark Resource as Completed" button in the action bar below.
 */
/**
 * Student-facing panel for a YouTube module whose owner has disabled off-site playback.
 *
 * Shown in place of the player once the SERVER has confirmed the video can't be embedded (IFrame
 * API error 101/150, re-verified against YouTube). Before this existed such a module was an
 * unrecoverable dead end: the video could not play, so no watch telemetry was ever produced, so the
 * watch-through completion gate could never be satisfied and the student could not advance past it.
 *
 * Watch time spent on youtube.com is NOT tracked — the student confirms completion themselves,
 * exactly as for an ExternalResource, and the module's question gates still apply.
 */
function YouTubeNotEmbeddablePanel({ watchUrl, onOpen }: { watchUrl: string; onOpen: () => void }) {
  return (
    <div className="w-full bg-[#0d0d14] px-7 py-10 flex flex-col items-center text-center gap-5">
      <div className="h-14 w-14 rounded-2xl bg-amber-500/15 border border-amber-500/25 flex items-center justify-center">
        <AlertTriangle className="h-6 w-6 text-amber-400" />
      </div>
      <div className="space-y-2 max-w-md">
        <h3 className="text-base font-semibold text-white">
          This video must be watched on YouTube
        </h3>
        <p className="text-sm text-white/55">
          Its owner has disabled playback on other websites, so it can't run inside the course
          player. Open it on YouTube, watch it to the end, then come back and mark this module
          complete.
        </p>
        <p className="text-xs text-white/35">
          Because the video plays on YouTube, your watch time isn't tracked for this module — the
          completion is on your word.
        </p>
      </div>
      <Button
        onClick={() => {
          onOpen();
          window.open(watchUrl, "_blank", "noopener,noreferrer");
        }}
        className="h-10 px-5 bg-white text-black hover:bg-white/90 font-medium"
      >
        <ExternalLink className="h-4 w-4 mr-1.5" />
        Watch on YouTube
      </Button>
    </div>
  );
}

function ExternalResourcePanel({ module, onOpen }: { module: any; onOpen: () => void }) {
  const hasUrl = !!module.external_url;

  return (
    <div className="w-full bg-[#0d0d14] px-7 py-10 flex flex-col items-center text-center gap-5">
      <div className="h-14 w-14 rounded-2xl bg-primary/15 border border-primary/25 flex items-center justify-center">
        <ExternalLink className="h-6 w-6 text-primary" />
      </div>

      <div className="space-y-2 max-w-2xl">
        {module.provider_name && (
          <span className="inline-block text-[11px] font-semibold uppercase tracking-widest text-primary/80 bg-primary/10 border border-primary/20 rounded-full px-3 py-1">
            {module.provider_name}
          </span>
        )}
        <h3 className="text-xl font-bold text-white">{module.title}</h3>
        {module.description && (
          <p className="text-sm text-white/50 leading-relaxed">{module.description}</p>
        )}
      </div>

      <div className="flex items-center gap-4 text-xs text-white/40">
        {typeof module.estimated_minutes === "number" && module.estimated_minutes > 0 && (
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" />
            Estimated {module.estimated_minutes} min
          </span>
        )}
        {hasUrl && (
          <span className="truncate max-w-[22rem]" title={module.external_url}>
            {(() => {
              try {
                return new URL(module.external_url).hostname;
              } catch {
                return module.external_url;
              }
            })()}
          </span>
        )}
      </div>

      {hasUrl ? (
        <Button
          onClick={onOpen}
          className="h-10 px-6 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          Open Resource
        </Button>
      ) : (
        // The server withholds external_url while the module is locked, and a misconfigured
        // module could have none at all — never render a dead button.
        <p className="text-xs text-amber-400/80">
          This resource has no link available yet. Contact your instructor.
        </p>
      )}
    </div>
  );
}

function ModuleMedia({
  module,
  serverResumeSeconds,
  userId,
  setVideoProgress,
  setIsPlaying,
  handleVideoEnd,
  setActiveVideoDuration,
  handleTimeUpdate,
  registerSeekTo,
  registerPause,
  registerPlay,
  onUnplayable,
  onResourceOpened,
}: any) {
  // Stable callback refs — prevents stale closures without recreating the player
  const handleVideoEndRef = useRef(handleVideoEnd);
  handleVideoEndRef.current = handleVideoEnd;

  const setVideoProgressRef = useRef(setVideoProgress);
  setVideoProgressRef.current = setVideoProgress;

  const setIsPlayingRef = useRef(setIsPlaying);
  setIsPlayingRef.current = setIsPlaying;

  const setActiveVideoDurationRef = useRef(setActiveVideoDuration);
  setActiveVideoDurationRef.current = setActiveVideoDuration;

  const handleTimeUpdateRef = useRef(handleTimeUpdate);
  handleTimeUpdateRef.current = handleTimeUpdate;

  // Furthest second the user has watched. Prefer serverResumeSeconds — openModule resolves it from
  // GET /training/resume/{id} before selecting the module, and unlike the cached progress list it is
  // clamped to the first unpassed in-video checkpoint and cannot be a value this page has already
  // written past. The cached fields remain as the fallback for when that lookup failed.
  //
  // This is a mount-time ref by design (a live value would fight the seek high-water mark it also
  // serves), which is why the authoritative number has to be resolved BEFORE the player mounts —
  // `key={selectedModule.id}` in the parent guarantees a fresh mount per module.
  const serverSeek =
    serverResumeSeconds ?? module.server_seek_position_seconds ?? module.active_watch_seconds ?? 0;
  const maxTimeRef = useRef<number>(module.isCompleted ? Infinity : serverSeek);
  // Duration in seconds, set once onDuration fires
  const durationRef = useRef<number>(0);

  // Player ref for imperative seeks (used by registerSeekTo)
  const ytPlayerSeekRef = useRef<((secs: number) => void) | null>(null);
  const ytPlayerPauseRef = useRef<(() => void) | null>(null);
  const ytPlayerPlayRef = useRef<(() => void) | null>(null);

  // Register the seek function with the hook so it can force server-controlled rewinds
  useEffect(() => {
    if (registerSeekTo) {
      registerSeekTo((secs: number) => {
        // Reset the high-water mark so the user cannot immediately skip forward again
        maxTimeRef.current = secs;
        ytPlayerSeekRef.current?.(secs);
      });
    }
    if (registerPause) {
      registerPause(() => {
        ytPlayerPauseRef.current?.();
      });
    }
    if (registerPlay) {
      registerPlay(() => {
        ytPlayerPlayRef.current?.();
      });
    }
  }, [registerSeekTo, registerPause, registerPlay]);

  // Keep maxTime in sync if the module is later marked completed
  useEffect(() => {
    if (module.isCompleted) maxTimeRef.current = Infinity;
  }, [module.isCompleted]);

  /* ── Derived flags ── */
  const hasVideo =
    (module.content_type === "youtube" ||
      module.content_type === "uploaded_video" ||
      module.content_type === "mixed") &&
    !!module.video_url;

  const videoUrl = sanitizeUrlValue(module.video_url);
  const ytId = videoUrl ? getYouTubeId(videoUrl) : null;
  const isCompleted: boolean = !!module.isCompleted;
  const allowSeek: boolean = !!module.allow_seek;

  // A URL that points at a video *site* rather than a media file. video.js can only play a media
  // stream, so handing it one of these produces MEDIA_ERR_SRC_NOT_SUPPORTED — and when the string
  // is malformed enough not to parse as absolute, video.js resolves it RELATIVE to our own origin
  // and the browser reports ERR_TOO_MANY_REDIRECTS against the LMS. Either way the student saw a
  // dead black player with the real cause visible only in the devtools console. Detected here so
  // an unplayable link fails with an explanation instead of falling through to video.js.
  const isVideoSiteUrl =
    !!videoUrl && /(?:youtube\.com|youtu\.be|youtube-nocookie\.com|vimeo\.com)/i.test(videoUrl);
  const isPlayableByVideoJs =
    !!videoUrl && !isVideoSiteUrl && (/^https?:\/\//i.test(videoUrl) || videoUrl.startsWith("/"));

  /* ── In-video checkpoints (pause + redirect to exam) ── */
  // Applies to BOTH YouTube and uploaded video; bypassed for completed modules and for the
  // instructor/QA `allow_seek` preview (consistent with the forward-seek restriction bypass).
  const navigate = useNavigate();
  const qc = useQueryClient();
  const videoCheckpoints = useVideoCheckpoints(module.id, hasVideo && !isCompleted && !allowSeek);
  const gateRedirectedRef = useRef(false);
  // Reflects the player's own readiness. The pause/seek below go through refs that stay null until
  // the player fires onReady, and calling them early is a silent no-op — the gate would be
  // consumed (gateRedirectedRef set) without the video ever actually pausing.
  const [playerReady, setPlayerReady] = useState(false);
  // Drives a visible hand-off overlay. The window between "gate reached" and the exams page
  // rendering involves a blocking write, and with no indicator a slow one is indistinguishable
  // from the feature being broken.
  const [handingOffToExam, setHandingOffToExam] = useState(false);
  useEffect(() => {
    const gate = videoCheckpoints.dueGate;
    if (!gate || gateRedirectedRef.current) return;
    // Wait for the player rather than firing into null refs. The effect re-runs when readiness
    // flips, so a gate that comes due during load is honoured a moment later instead of dropped.
    if (!playerReady) return;
    gateRedirectedRef.current = true;

    // Snap to the exact gate second (covers 2x-speed overshoot), lock the seek high-water mark
    // there, and pause — then hand off to the gate's exam.
    maxTimeRef.current = gate.trigger_at_seconds;
    ytPlayerSeekRef.current?.(gate.trigger_at_seconds);

    // Pin the tracked position to the gate second BEFORE pausing.
    //
    // Pausing fires onPause, which makes useTraining report the position it is currently holding.
    // That value comes from the last timeupdate, which the seek above has not produced yet — so it
    // can still be a fraction BELOW the gate (26.8 → the server floors it to 26). The exam's unlock
    // rule is `LastWatchedPositionSeconds >= TriggerAtSeconds`, so a report of 26 landing after the
    // arrival write below re-locks the very exam we are redirecting to, and the student is stuck:
    // the video will not continue and the exam will not open. Pinning first makes the pause report
    // and the arrival write carry the identical second, so their order stops mattering.
    handleTimeUpdateRef.current?.(gate.trigger_at_seconds);

    ytPlayerPauseRef.current?.();

    // Remember which module to reopen when the student returns from the exam (see TrainingPage).
    try {
      sessionStorage.setItem("pending_video_checkpoint_module", module.id);
    } catch {
      /* storage disabled (private mode) — the student can simply reopen the module manually */
    }

    toast.info(`Checkpoint reached: ${gate.name}. Pass the exam to continue watching.`);

    // Persist arrival at the gate BEFORE navigating, and wait for it.
    //
    // This is load-bearing, not an optimisation. The exam's unlock rule is "the student has reached
    // TriggerAtSeconds", read from the stored watch position — but the routine heartbeat only fires
    // every 5s and only while playing (use-training.ts), and we have just paused. So the arrival at
    // the gate would frequently never be written, the server would still see the pre-gate position,
    // and the student would be sent to an exam it considered not-yet-reached: a deadlock in which
    // the video cannot continue and the exam cannot be opened.
    //
    // Reporting the gate timestamp cannot be used to skip ahead: the server clamps any reported
    // position to the first unpassed gate (TrainingService.GetVideoCheckpointCeilingAsync), so this
    // value is exactly the ceiling and a tampered-with larger one lands on the same second.
    // Awaiting it also stops the in-flight request being cancelled by the unmount, which is what
    // left an abandoned YouTube watchtime request pending in the network panel.
    //
    // The wait is BOUNDED. Blocking the redirect on an unbounded fetch means one stalled request
    // leaves the student on a paused video with no exam and no error — the same dead end this
    // whole hand-off exists to prevent. On timeout we navigate anyway: the exam may still be
    // locked, but "locked with a reason" is a recoverable state the student can act on, whereas
    // hanging here forever is not.
    setHandingOffToExam(true);
    void (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        await incrementActiveWatch(
          {
            moduleId: module.id,
            durationSeconds: durationRef.current || undefined,
            isEnded: false,
            positionSeconds: gate.trigger_at_seconds,
          },
          controller.signal,
        );
      } catch (err) {
        // Best-effort: a failed write leaves the exam locked, which is the safe direction. The
        // student can reopen the module and the next heartbeat will record the position. Logged
        // rather than swallowed silently so this is diagnosable when it does happen.
        console.warn("Failed to persist checkpoint arrival before opening the exam", err);
      } finally {
        clearTimeout(timeout);
      }
      // The training page's available-exams cache still holds the pre-arrival state where
      // this exam is locked ("video_checkpoint_not_reached"). Without refreshing it, the
      // exams page can read stale data and immediately show "locked" even though the watch
      // position is now persisted — the auto-select useEffect fires on the stale snapshot
      // and shows a spurious "locked" error toast.
      //
      // `userId` is the prop, NOT `user?.id`. ModuleMedia is a top-level component, so `user`
      // (from useAuth in TrainingPage) is not in scope here — referencing it threw a
      // ReferenceError that the catch below swallowed, which meant this invalidation had never
      // actually run on the checkpoint hand-off despite being load-bearing.
      try {
        await invalidateAllStudentState(qc, userId);
      } catch {
        // Best-effort — navigate anyway; the exam page can show a recoverable lock state.
      }
      navigate({ to: "/exams", search: { examId: gate.exam_id } });
    })();
  }, [videoCheckpoints.dueGate, playerReady]);

  /* ── Shared progress callback ── */
  // Only the *displayed* value is pushed upward, and only when it actually changes.
  //
  // The player ticks 4x/second, and `videoProgress` lives in useTraining — top-level page state.
  // Pushing every tick re-rendered the whole training page (module list, checkpoint lock
  // computation, render list) four times a second for the entire duration of every video, competing
  // with playback for the main thread. The consumer only renders `Math.round(videoProgress)` and a
  // `>= 99.9` badge, so anything finer than that was invisible work. Bucketing on exactly what the
  // UI can show collapses ~4/sec into ~100 updates for a whole video with identical output; the
  // separate 1000 bucket keeps the 99.9 threshold exact rather than letting rounding decide it.
  const lastPushedBucketRef = useRef<number>(-1);
  const handleProgress = (pct: number) => {
    const bucket = pct >= 99.9 ? 1000 : Math.round(pct);
    if (bucket !== lastPushedBucketRef.current) {
      lastPushedBucketRef.current = bucket;
      setVideoProgressRef.current(pct);
    }
    // Back-calculate current second from pct so maxTimeRef stays accurate. Deliberately NOT
    // throttled — the checkpoint gate and end-of-video detection must see every tick.
    if (durationRef.current > 0) {
      const sec = (pct / 100) * durationRef.current;
      if (sec > maxTimeRef.current && !isCompleted) maxTimeRef.current = sec;
      videoCheckpoints.checkTime(sec);
    }
    // `!isCompleted` mirrors the video.js branch below, which has always had this guard — the
    // YouTube branch not having it is what let a replay of a finished module re-run completion
    // (which navigates to the next module or closes the player) instead of simply playing.
    // Reaching the genuine end of a replay still fires the player's own `onEnded`.
    if (!isCompleted && pct >= 99.9) {
      handleVideoEndRef.current?.();
    }
  };

  // Visible hand-off between "gate reached" and the exam page. The gate pauses the video, shows a
  // toast, persists arrival and then routes — all of which previously happened with no indicator,
  // so any delay in the middle looked exactly like the feature silently failing.
  const withGateOverlay = (player: React.ReactNode) => (
    <div className="relative w-full h-full">
      {player}
      {handingOffToExam && (
        <div className="absolute inset-0 z-40 flex flex-col items-center justify-center gap-3 bg-black/75 px-6 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-white/85" />
          <p className="text-sm font-medium text-white">Opening your checkpoint exam…</p>
        </div>
      )}
    </div>
  );

  /* ── YouTube video the owner has blocked from embedding ──
     Rendered INSTEAD of the player: the server has already established this video cannot play
     here, so building a player would only reproduce the same 101/150 error and show the student a
     black rectangle before failing. `requires_manual_completion` is the server's decision, never
     re-derived locally. */
  if (hasVideo && ytId && module.requires_manual_completion) {
    return withGateOverlay(
      <YouTubeNotEmbeddablePanel
        watchUrl={videoUrl ?? `https://www.youtube.com/watch?v=${ytId}`}
        onOpen={() => onResourceOpened?.(module.id)}
      />,
    );
  }

  /* ── YouTube video ── */
  if (hasVideo && ytId) {
    const initialProgress = module.isCompleted ? 100 : Math.round(module.progressPercent || 0);
    const initialTimeSecs = isCompleted
      ? 0
      : maxTimeRef.current === Infinity
        ? 0
        : maxTimeRef.current;
    return withGateOverlay(
      <YouTubePlayer
        key={ytId}
        videoId={ytId}
        initialTime={initialTimeSecs}
        initialProgress={initialProgress}
        isCompleted={isCompleted}
        allowSeek={allowSeek}
        className="w-full h-full"
        onPlay={() => setIsPlayingRef.current(true)}
        onPause={() => setIsPlayingRef.current(false)}
        onDuration={(secs) => {
          durationRef.current = secs;
          setActiveVideoDurationRef.current(secs);
        }}
        onProgress={handleProgress}
        onTimeUpdate={(secs) => {
          handleTimeUpdateRef.current?.(secs);
          videoCheckpoints.checkTime(secs);
        }}
        onEnded={() => handleVideoEndRef.current?.()}
        onSeekToReady={(seekFn) => {
          ytPlayerSeekRef.current = seekFn;
        }}
        onPauseReady={(pauseFn) => {
          ytPlayerPauseRef.current = pauseFn;
        }}
        onPlayReady={(playFn) => {
          ytPlayerPlayRef.current = playFn;
        }}
        onReadyChange={setPlayerReady}
        onUnplayable={(info) => onUnplayable?.(module.id, info)}
      />,
    );
  }

  /* ── Unplayable link ──
     Sits between the two players deliberately: reaching here means the module claims to have a
     video but the URL is neither a resolvable YouTube ID nor something video.js can load, so
     falling through would hand video.js a source guaranteed to fail. */
  if (hasVideo && !isPlayableByVideoJs) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-3 bg-black px-6 text-center">
        <AlertTriangle className="h-8 w-8 text-amber-400" />
        <p className="text-sm font-medium text-white">
          {isVideoSiteUrl
            ? "This module's video link could not be read, so the video cannot be played."
            : "This module's video link is not a valid address, so the video cannot be played."}
        </p>
        <p className="text-xs text-white/45">
          Please report this to your coordinator — the link needs to be corrected on the module.
        </p>
      </div>
    );
  }

  /* ── Uploaded / HLS video (video.js) ── */
  if (hasVideo) {
    const src = videoUrl!;
    return withGateOverlay(
      <VideoPlayer
        src={src}
        isCompleted={isCompleted}
        allowSeek={allowSeek}
        initialTime={maxTimeRef.current === Infinity ? 0 : maxTimeRef.current}
        onTimeUpdate={(t, d) => {
          if (!d || isNaN(d) || d <= 0) return;
          durationRef.current = d;
          setActiveVideoDurationRef.current(Math.floor(d));
          if (t > maxTimeRef.current && !isCompleted) maxTimeRef.current = t;
          // maxTimeRef.current is Infinity for completed modules (a sentinel that disables seek
          // restriction) — never use it directly for progress, or it computes as Infinity%.
          const watched = isCompleted ? t : Math.min(maxTimeRef.current, d);
          const pct = (watched / d) * 100;
          // Same display-bucket throttle as the YouTube path — video.js fires timeupdate several
          // times a second, and this state lives at the top of the page.
          const bucket = pct >= 99.9 ? 1000 : Math.round(pct);
          if (bucket !== lastPushedBucketRef.current) {
            lastPushedBucketRef.current = bucket;
            setVideoProgressRef.current(pct);
          }
          handleTimeUpdateRef.current?.(t);
          videoCheckpoints.checkTime(t);
          if (!isCompleted && pct >= 99.9) handleVideoEndRef.current?.();
        }}
        onPlay={() => setIsPlayingRef.current(true)}
        onPause={() => setIsPlayingRef.current(false)}
        onEnded={() => {
          setIsPlayingRef.current(false);
          setVideoProgressRef.current(100);
          handleVideoEndRef.current?.();
        }}
        onSeekToReady={(seekFn) => {
          ytPlayerSeekRef.current = seekFn;
        }}
        onPauseReady={(pauseFn) => {
          ytPlayerPauseRef.current = pauseFn;
        }}
        onPlayReady={(playFn) => {
          ytPlayerPlayRef.current = playFn;
        }}
        onReadyChange={setPlayerReady}
        className="w-full h-full"
      />,
    );
  }

  /* ── Fallback ── */
  return (
    <div className="w-full h-full flex flex-col items-center justify-center text-white space-y-4">
      <PlayCircle className="h-16 w-16 opacity-20" />
      <p className="text-muted-foreground">Video content not available</p>
    </div>
  );
}
